import { useEffect, useRef, useState } from 'react'
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  interpolate,
  Extrapolate,
  withSpring,
} from 'react-native-reanimated'
import { useMedia, useThemeName } from 'tamagui'
import tamaguiConfig from '../../../../config/tamagui.config'

import { selectMediaValue } from './utils'

export function useHeroAnimation() {
  const media = useMedia()
  const themeName = useThemeName()
  const { reducedMotion: reducedEffects } = media

  const rotateAnimation = useSharedValue(35)
  const translateYAnimation = useSharedValue(100)
  const opacityAnimation = useSharedValue(0)

  const guildCardTranslateZAnimation = useSharedValue(0)
  const guildCardTranslateXAnimation = useSharedValue(0)
  const guildCardTranslateYAnimation = useSharedValue(0)

  const eventCardTranslateZAnimation = useSharedValue(0)
  const eventCardTranslateXAnimation = useSharedValue(0)

  const guildCardRef = useRef<any>(null)
  const gradientContainerRef = useRef<any>(null)

  const [gradient, setGradient] = useState<{
    play: () => void
    pause: () => void
    resize: () => void
  } | null>(null)

  // Gradient play/pause based on visibility
  useEffect(() => {
    if (
      gradientContainerRef.current &&
      gradient &&
      typeof window !== 'undefined' &&
      window.IntersectionObserver
    ) {
      const intersectionObserver = new window.IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            gradient.play()
          } else {
            gradient.pause()
          }
        }
      )

      intersectionObserver.observe(
        gradientContainerRef.current as unknown as Element
      )

      return () => {
        intersectionObserver.disconnect()
      }
    }
  }, [gradient])

  // Card 3D animation triggered by scroll visibility
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (guildCardRef.current) {
        const springOptions = { mass: 1, stiffness: 50, damping: 10 }

        const triggerAnimation = () => {
          rotateAnimation.value = withSpring(
            selectMediaValue(media, [35, 35, 25]),
            springOptions
          )
          translateYAnimation.value = withSpring(
            selectMediaValue(media, [-350, -350, -100]),
            springOptions
          )
          opacityAnimation.value = withSpring(1.0, {
            ...springOptions,
            overshootClamping: true,
          })

          guildCardTranslateZAnimation.value = withSpring(100, springOptions)
          guildCardTranslateXAnimation.value = withSpring(
            selectMediaValue(media, [-25, -25, -25, -35]),
            springOptions
          )
          guildCardTranslateYAnimation.value = withSpring(
            selectMediaValue(media, [-30, -30, -30, -75]),
            springOptions
          )

          eventCardTranslateZAnimation.value = withSpring(100, springOptions)
          eventCardTranslateXAnimation.value = withSpring(
            selectMediaValue(media, [25, 25, 35, 50, 60]),
            springOptions
          )
        }

        // On mobile, the card is inside an overflow:hidden container so the
        // IntersectionObserver can never detect it. Trigger immediately.
        if (!media.gtSm) {
          triggerAnimation()
          return
        }

        if (!window.IntersectionObserver) return

        let observerConnected = true

        const intersectionObserver = new window.IntersectionObserver(
          ([entry]) => {
            if (entry.intersectionRatio >= 0.4) {
              triggerAnimation()
              intersectionObserver.disconnect()
              observerConnected = false
            }
          },
          { threshold: [0, 0.4, 1.0] }
        )

        intersectionObserver.observe(guildCardRef.current as unknown as Element)

        return () => {
          if (observerConnected) {
            intersectionObserver.disconnect()
          }
        }
      }
    }
  }, [guildCardRef, media.gtXs, media.gtSm, media.gtMd, media.gtLg])

  const cardStyle = useAnimatedStyle(() => {
    if (reducedEffects) {
      return {
        paddingHorizontal: 40,
        transform: [{ translateY: translateYAnimation.value }],
        opacity: opacityAnimation.value,
      } as unknown as import('react-native').ViewStyle
    }
    return {
      paddingHorizontal: 40,

      transform: [
        { perspective: 1000 },
        { rotateX: `${rotateAnimation.value}deg` },
        { translateZ: -250 },
        {
          translateY: translateYAnimation.value,
        },
      ],
      opacity: opacityAnimation.value,
    } as unknown as import('react-native').ViewStyle
  }, [reducedEffects])

  const bigCardStyle = useAnimatedStyle(() => {
    const base = {
      transformOrigin: 'top',
      backgroundColor: themeName === 'dark'
        ? tamaguiConfig.themes.dark.backgroundSoft.val
        : tamaguiConfig.themes.light.backgroundSoft.val,
      paddingTop: 32,
      paddingBottom: 16,
      paddingHorizontal: 16,
      borderRadius: 32,
      boxShadow: '0px 0px 12px rgba(10, 37, 64, 0.08)',
      opacity: opacityAnimation.value,
    }
    if (reducedEffects) {
      return {
        ...base,
        transform: [{ translateY: translateYAnimation.value }],
      } as unknown as import('react-native').ViewStyle
    }
    return {
      ...base,
      transform: [
        { perspective: 1000 },
        { rotateX: `${rotateAnimation.value}deg` },
        { translateZ: -250 },
        {
          translateY: translateYAnimation.value,
        },
      ],
    } as unknown as import('react-native').ViewStyle
  }, [themeName, reducedEffects])

  const guildCardStyle = useAnimatedStyle(() => {
    const base = {
      borderRadius: 32,
      overflow: 'hidden',
      zIndex: 2,
    }
    if (reducedEffects) {
      return {
        ...base,
        transform: [
          { translateX: guildCardTranslateXAnimation.value },
          { translateY: guildCardTranslateYAnimation.value },
        ],
        boxShadow: '18px 24px 24px rgba(10, 37, 64, 0.16)',
      } as unknown as import('react-native').ViewStyle
    }

    const shadowOpacity = interpolate(
      guildCardTranslateZAnimation.value,
      [0, 100],
      [0.08, 0.16],
      Extrapolate.EXTEND
    )
    const shadowBlur = interpolate(
      guildCardTranslateZAnimation.value,
      [0, 100],
      [12, 24],
      Extrapolate.EXTEND
    )
    const shadowOffsetX = interpolate(
      guildCardTranslateZAnimation.value,
      [0, 100],
      [0, 18],
      Extrapolate.EXTEND
    )
    const shadowOffsetY = interpolate(
      guildCardTranslateZAnimation.value,
      [0, 100],
      [0, 24],
      Extrapolate.EXTEND
    )

    return {
      ...base,
      transform: [
        { perspective: 500 },
        { translateZ: guildCardTranslateZAnimation.value },
        { translateX: guildCardTranslateXAnimation.value },
        { translateY: guildCardTranslateYAnimation.value },
      ],
      boxShadow: `${shadowOffsetX}px ${shadowOffsetY}px ${shadowBlur}px rgba(10, 37, 64, ${shadowOpacity})`,
    } as unknown as import('react-native').ViewStyle
  }, [reducedEffects])

  const eventCardStyle = useAnimatedStyle(() => {
    const base = {
      borderRadius: 32,
      overflow: 'hidden',
      zIndex: 2,
      marginVertical: 12,
    }
    if (reducedEffects) {
      return {
        ...base,
        transform: [
          { translateX: eventCardTranslateXAnimation.value },
        ],
        boxShadow: '-9px 24px 24px rgba(10, 37, 64, 0.16)',
      } as unknown as import('react-native').ViewStyle
    }

    const eventShadowOpacity = interpolate(
      guildCardTranslateZAnimation.value,
      [0, 100],
      [0.08, 0.16],
      Extrapolate.EXTEND
    )
    const eventShadowBlur = interpolate(
      guildCardTranslateZAnimation.value,
      [0, 100],
      [12, 24],
      Extrapolate.EXTEND
    )
    const eventShadowOffsetX = interpolate(
      guildCardTranslateZAnimation.value,
      [0, 100],
      [0, -9],
      Extrapolate.EXTEND
    )
    const eventShadowOffsetY = interpolate(
      guildCardTranslateZAnimation.value,
      [0, 100],
      [0, 24],
      Extrapolate.EXTEND
    )

    return {
      ...base,
      transform: [
        { perspective: 500 },
        { translateZ: eventCardTranslateZAnimation.value },
        { translateX: eventCardTranslateXAnimation.value },
      ],
      boxShadow: `${eventShadowOffsetX}px ${eventShadowOffsetY}px ${eventShadowBlur}px rgba(10, 37, 64, ${eventShadowOpacity})`,
    } as unknown as import('react-native').ViewStyle
  }, [reducedEffects])

  return {
    media,
    gradientContainerRef,
    guildCardRef,
    gradient,
    setGradient,
    cardStyle,
    bigCardStyle,
    guildCardStyle,
    eventCardStyle,
  }
}

export type HeroAnimation = ReturnType<typeof useHeroAnimation>
